import { createSelector } from '@reduxjs/toolkit';
import { DefaultRootState } from 'react-redux';
import { EPlatformsNames, EStripeSubStatus, ETrialStatus } from '../../../models/consts';
import { EChatgptPlanNames } from '../../../models/paymentPlans/chatgpt-payments-plan';
import { IPlatformSubscriptionRow } from '../../../views/UserPages/Subscription/Subscription';
import { PlatformsState } from '../../reducers/platform';

export const getChatgptReposSelector = createSelector((state: DefaultRootState) => state.platform, ({ chatgpt }: PlatformsState) => chatgpt.items);
export const getChatgptTotal = createSelector((state: DefaultRootState) => state.platform, ({ chatgpt }: PlatformsState) => chatgpt.totalItems);

export const getChatgptCanUserBackup = createSelector((state: DefaultRootState) => state.platform, ({ chatgpt }: PlatformsState) => chatgpt.canUserBackup);

export const isChatgptRepoLoadingSelector = createSelector((state: DefaultRootState) => state.platform, ({ chatgpt }: PlatformsState) => chatgpt.itemsLoading);

export const isChatgptUsersLoadingSelector = createSelector((state: DefaultRootState) => state.platform, ({ chatgpt }: PlatformsState) => chatgpt.usersLoading);

export const getChatgptUsersSelector = createSelector((state: DefaultRootState) => state.platform, ({ chatgpt }: PlatformsState) => chatgpt.users);
export const isChatgptConnect = createSelector((state: DefaultRootState) => state.platform, ({ chatgpt }: PlatformsState) => chatgpt.isConnectionActive);

export const chatgptTrialStatus = createSelector((state: DefaultRootState) => state.platform, ({ chatgpt }: PlatformsState) => chatgpt.trial?.trialStatus);

export const chatgptTrialHoursLeft = createSelector((state: DefaultRootState) => state.platform, ({ chatgpt }: PlatformsState) => {
  return chatgpt.trial?.trialHoursLeft || null;
});

export const chatgptTrialQuantityLeft = createSelector((state: DefaultRootState) => state.platform, ({ chatgpt }: PlatformsState) => {
  return chatgpt.trial?.availableItemsQuantity || null;
});

export const chatgptTrialPlanQuantity = createSelector((state: DefaultRootState) => state.platform, ({ chatgpt }: PlatformsState) => {
  return chatgpt.trial?.planQuantity || 0;
});

export const chatgptSubInfo = createSelector((state: DefaultRootState) => state.platform, ({ chatgpt }: PlatformsState): IPlatformSubscriptionRow | null => {
  const { subscription } = chatgpt;
  if (subscription) {
    return {
      id: 7,
      platformName: EPlatformsNames.CHATGPT,
      plan: subscription?.planName || '',
      price: `$${subscription?.cost.toFixed(2)} / ${subscription?.isAnnual ? 'yr.' : 'mo.'}`,
      status: subscription?.status,
      quantity: subscription.quantity,
      planQuantity: subscription.planQuantity,
      addedItemsQuantity: subscription.addedItemsQuantity,
      isCancel: subscription.isCancel,
      isConnect: false
    };
  }
  return null;
});

export const chatgptTrialInfo = createSelector((state: DefaultRootState) => state.platform, ({ chatgpt }: PlatformsState): IPlatformSubscriptionRow | null => {
  const { trial } = chatgpt;
  if (trial) {
    return {
      id: 7,
      platformName: EPlatformsNames.CHATGPT,
      plan: EChatgptPlanNames.TRIAL,
      price: `-`,
      status: trial.trialStatus,
      quantity: 0,
      planQuantity: 0,
      addedItemsQuantity: 0,
      isCancel: false,
      isConnect: false
    };
  }
  return null;
});

export const chatgptSubscription = createSelector((state: DefaultRootState) => state.platform, ({ chatgpt }: PlatformsState) => chatgpt.subscription);

export const chatgptTrialData = createSelector((state: DefaultRootState) => state.platform, ({ chatgpt }: PlatformsState) => {
  return chatgpt.trial
});

export const chatgptActualPlan = createSelector((state: DefaultRootState) => state.platform, ({ chatgpt }: PlatformsState) => {
  const { subscription: chatgptSub, trial: chatgptTrial, isConnectionActive } = chatgpt;

  const chatgptItem: IPlatformSubscriptionRow = {
    id: 7,
    platformName: EPlatformsNames.CHATGPT,
    plan: '-',
    price: '-',
    quantity: 0,
    planQuantity: 0,
    addedItemsQuantity: chatgptTrial?.addedItemsQuantity || 0,
    status: EStripeSubStatus.INACTIVE,
    isCancel: false,
    finishDateInMillisecond: null,
    isConnect: isConnectionActive
  };

  if (chatgptTrial && chatgptTrial.trialStatus === ETrialStatus.NOT_STARTED) {
    chatgptItem.plan = EChatgptPlanNames.TRIAL;
    chatgptItem.status = chatgptTrial.trialStatus;
    chatgptItem.quantity = chatgptTrial.availableItemsQuantity || 0;
    chatgptItem.addedItemsQuantity = chatgptTrial.addedItemsQuantity || 0;
    chatgptItem.planQuantity = chatgptTrial.planQuantity || 0;
    chatgptItem.finishDate = chatgptTrial.finishDate || null;
    chatgptItem.isConnect = isConnectionActive
  }

  if (chatgptTrial && chatgptTrial.trialStatus === ETrialStatus.STARTED) {
    chatgptItem.plan = EChatgptPlanNames.TRIAL;
    chatgptItem.status = chatgptTrial.trialStatus;
    chatgptItem.quantity = chatgptTrial.availableItemsQuantity || 0;
    chatgptItem.addedItemsQuantity = chatgptTrial.addedItemsQuantity || 0;
    chatgptItem.planQuantity = chatgptTrial.planQuantity || 0;
    chatgptItem.finishDate = chatgptTrial.finishDate || null;
    chatgptItem.isConnect = isConnectionActive
  }

  if (chatgptSub && (!chatgptTrial || chatgptTrial?.trialStatus !== ETrialStatus.STARTED || chatgptSub?.status === EStripeSubStatus.ACTIVE)) {
    chatgptItem.plan = chatgptSub.planName || '';
    chatgptItem.status = chatgptSub.status;
    chatgptItem.quantity = chatgptSub.quantity || 0;
    chatgptItem.finishDate = chatgptSub.finishDate || null;
    chatgptItem.planQuantity = chatgptSub.planQuantity || 0;
    chatgptItem.addedItemsQuantity = chatgptSub.addedItemsQuantity || 0;
    chatgptItem.price = `$${chatgptSub.cost.toFixed(2)} / ${chatgptSub.isAnnual ? 'yr.' : 'mo.'}`
    chatgptItem.isCancel = chatgptSub.isCancel;
    chatgptItem.finishDateInMillisecond = chatgptSub.finishDateInMillisecond;
    chatgptItem.isConnect = isConnectionActive
  }

  return chatgptItem
});
